import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Trash2, ExternalLink } from "lucide-react";
import { API } from "../../lib/site";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", published: false };

const inputCls =
  "w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition";
const labelCls = "block font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mb-1.5";

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/blog`, { withCredentials: true }).then((res) => setPosts(res.data.posts || [])).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const startNew = () => { setForm(EMPTY); setEditing({}); };
  const startEdit = (p) => {
    setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image, published: p.published });
    setEditing(p);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/admin/blog/${editing.id}`, form, { withCredentials: true });
        toast.success("Article updated.");
      } else {
        await axios.post(`${API}/admin/blog`, form, { withCredentials: true });
        toast.success("Article created.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true });
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (editing) {
    return (
      <div data-testid="admin-blog-editor" className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8 max-w-3xl">
        <button
          data-testid="admin-blog-back-btn"
          onClick={() => setEditing(null)}
          className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-500 hover:text-reef transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> All articles
        </button>
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Title *</label>
            <input data-testid="admin-blog-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Best Blackout Curtains for Dubai Summers" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>URL slug (auto if empty)</label>
              <input data-testid="admin-blog-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="best-blackout-curtains-dubai" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Cover image URL</label>
              <input data-testid="admin-blog-cover-input" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Excerpt (shown on the blog list)</label>
            <textarea data-testid="admin-blog-excerpt-input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`${inputCls} resize-y`} />
          </div>
          <div>
            <label className={labelCls}>Article content — separate paragraphs with a blank line</label>
            <textarea data-testid="admin-blog-content-input" rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputCls} resize-y`} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-blog-publish-toggle"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="w-4 h-4 accent-[#865F34]"
            />
            <span className="text-sm text-slate-700">Published (visible on /blog)</span>
          </label>
          <button
            data-testid="admin-blog-save-btn"
            onClick={save}
            disabled={saving}
            className="px-8 py-3 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : editing.id ? "Update article" : "Create article"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-list">
      <button
        data-testid="admin-blog-new-btn"
        onClick={startNew}
        className="mb-6 flex items-center gap-2 px-6 py-3 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors"
      >
        <Plus className="w-4 h-4" /> New article
      </button>
      {posts.length === 0 && (
        <p data-testid="admin-blog-empty" className="text-sm text-slate-500 italic py-10 text-center">No articles yet — write your first one.</p>
      )}
      <div className="space-y-3">
        {posts.map((p, i) => (
          <div key={p.id} data-testid={`admin-blog-row-${i}`} className="bg-white border border-sand-border rounded-2xl p-5 flex items-center gap-4">
            {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-12 rounded-lg object-cover border border-sand-border shrink-0 hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-slate-900 truncate">{p.title}</p>
              <p className="font-mono text-[11px] text-slate-400 mt-0.5">/blog/{p.slug}</p>
            </div>
            <span className={`px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.1em] uppercase shrink-0 ${
              p.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"
            }`}>
              {p.published ? "Live" : "Draft"}
            </span>
            {p.published && (
              <a data-testid={`admin-blog-view-${i}`} href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-reef transition-colors" aria-label="View article">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button data-testid={`admin-blog-edit-${i}`} onClick={() => startEdit(p)} className="px-4 py-2 rounded-full border border-sand-border text-slate-600 font-mono text-[10px] tracking-[0.12em] uppercase hover:border-reef/40 hover:text-reef transition-colors">
              Edit
            </button>
            <button data-testid={`admin-blog-delete-${i}`} onClick={() => remove(p.id)} className="p-2 text-slate-300 hover:text-red-600 transition-colors" aria-label="Delete article">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
